import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormArray, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClientModule } from '@angular/common/http';
import { RecepcionesService } from '../../Service/recepciones-service';
import { CotizacionesService } from '../../Service/cotizaciones-service';
import { ToastService } from '../../Service/toast-service';
import { UdcsService } from '../../Service/udcs-service';

@Component({
  selector: 'app-cotizaciones-add',
  imports: [CommonModule, ReactiveFormsModule, FormsModule, HttpClientModule],
  templateUrl: './cotizaciones-add.html',
  styleUrl: './cotizaciones-add.css',
})
export class CotizacionesAdd implements OnInit {
  public cotizacionForm!: FormGroup;
  public idRecepcion!: number;
  public datosUnidad: any = null;
  public ivaIncluido: boolean = false; 
  public intentarGuardar: boolean = false;

  public datosCarroceriaPatio: any[] = [];
  public currentVehicleSvg: string = 'images/croquis-sedan.svg';

  private mapperCarroceria: { [key: string]: string } = {
    'Sedán': 'sedan', 
    'Pickup': 'pickup', 
    'SUV': 'suv', 
    'Camión': 'camion', 
    'Autobús': 'autobus'
  };  

  listaUdcsGravedad: any[] = [];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private recepcionesService: RecepcionesService,
    private cotizacionesService: CotizacionesService,
    public toastService: ToastService,
    private cdr: ChangeDetectorRef,
    private udcsService: UdcsService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.idRecepcion = +params['recepcionId'];
      if (!this.idRecepcion) {
        this.toastService.showDanger('Error de Flujo', 'No se especificó ninguna recepción base.');
        this.router.navigate(['/recepciones']);
        return;
      }
      this.inicializarFormulario();
      this.cargarDatosContextoRecepcion();
      this.cargarTarifasUdc();
    });
  }

  private inicializarFormulario(): void {
    this.cotizacionForm = this.fb.group({
      idRecepcion: [this.idRecepcion, Validators.required],
      vigenciaDias: [15, [Validators.required, Validators.min(1)]],
      subtotal: [{ value: 0, disabled: true }],
      iva: [{ value: 0, disabled: true }],
      total: [{ value: 0, disabled: true }],
      comentariosCliente: [''],
      lineas: this.fb.array([])
    });

    // 🚀 Forzar Mayúsculas en Notas/Comentarios Generales
    this.cotizacionForm.get('comentariosCliente')?.valueChanges.subscribe(valor => {
      if (valor && valor !== valor.toUpperCase()) {
        this.cotizacionForm.get('comentariosCliente')?.setValue(valor.toUpperCase(), { emitEvent: false });
      }
    });    
  }

  get lineas(): FormArray {
    return this.cotizacionForm.get('lineas') as FormArray;
  }

  public agregarLinea(tipo: 'Refaccion' | 'Mano de Obra' | 'Servicio Externo', desc = '', precio = 0, itemLink: number | null = null): void {
    const nuevaLinea = this.fb.group({
      tipoItem: [tipo, Validators.required],
      codigoInterno: [''],
      descripcion: [desc, Validators.required],
      cantidad: [1, [Validators.required, Validators.min(0.01)]],
      precioUnitario: [precio, [Validators.required, Validators.min(0)]],
      descuento: [0, [Validators.required, Validators.min(0)]],
      subtotalLinea: [{ value: 0, disabled: true }],
      aprobadoPorCliente: [true],
      idCarroceriaLink: [itemLink] // Enlace Croquis (Modalidad A) o Null (Modalidad B)
    });

    // 🚀 Forzar Mayúsculas en la descripción de la partida en tiempo real
    nuevaLinea.get('descripcion')?.valueChanges.subscribe(valor => {
      if (valor && valor !== valor.toUpperCase()) {
        nuevaLinea.get('descripcion')?.setValue(valor.toUpperCase(), { emitEvent: false });
      }
    });

    // Suscripción reactiva a cambios matemáticos en la fila
    nuevaLinea.valueChanges.subscribe(() => this.calcularTotales());
    this.lineas.push(nuevaLinea);
    this.calcularTotales();
  }

  public removerLinea(index: number): void {
    this.lineas.removeAt(index);
    this.calcularTotales();
  }

  public conmutarIvaSwitch(): void {
    // Si el usuario cambia el switch, recalculamos los unitarios inversamente para no alterar el valor final
    this.lineas.controls.forEach(control => {
      const precioActual = control.get('precioUnitario')?.value || 0;
      if (this.ivaIncluido) {
        // De Neto a Bruto (Visual)
        control.get('precioUnitario')?.setValue(+(precioActual * 1.16).toFixed(2), { emitEvent: false });
      } else {
        // De Bruto a Neto (Base DB)
        control.get('precioUnitario')?.setValue(+(precioActual / 1.16).toFixed(2), { emitEvent: false });
      }
    });
    this.calcularTotales();
  }

  private cargarTarifasUdc(): void {
    console.log('🛰️ [SaaS Rampa] Descargando matriz de tarifas UDC (TALLER/GRAVEDAD)...');
    this.udcsService.getTarifasUdc().subscribe({
      next: (data) => {
        this.listaUdcsGravedad = data; 
        this.cdr.detectChanges();
      },
      error: (err) => console.error('Error al obtener marcas UDC', err)
    });    
  }

  private calcularTotales(): void {
    let subtotalAcumulado = 0;

    this.lineas.controls.forEach(control => {
      const cantidad = control.get('cantidad')?.value || 0;
      let precioUnitario = control.get('precioUnitario')?.value || 0;
      const descuento = control.get('descuento')?.value || 0;
      const aprobado = control.get('aprobadoPorCliente')?.value;

      // Si visualmente está activo el switch de IVA incluido, extraemos el neto para el cálculo base del ERP
      if (this.ivaIncluido) {
        precioUnitario = precioUnitario / 1.16;
      }

      const subtotalLinea = (cantidad * precioUnitario) - descuento;
      control.get('subtotalLinea')?.setValue(+subtotalLinea.toFixed(2), { emitEvent: false });

      if (aprobado) {
        subtotalAcumulado += subtotalLinea;
      }
    });

    const ivaCalculado = subtotalAcumulado * 0.16;
    const totalCalculado = subtotalAcumulado + ivaCalculado;

    this.cotizacionForm.patchValue({
      subtotal: +subtotalAcumulado.toFixed(2),
      iva: +ivaCalculado.toFixed(2),
      total: +totalCalculado.toFixed(2)
    }, { emitEvent: false });
  }

  private cargarDatosContextoRecepcion(): void {
    console.log('🛰️ Extrayendo croquis y expediente JDE del patio para ID:', this.idRecepcion);
    
    this.recepcionesService.getRecepcionesList().subscribe({
      next: (todas: any[]) => {
        const recepcionActiva = todas.find(r => r.id === this.idRecepcion);
        
        if (recepcionActiva) {
          this.datosUnidad = recepcionActiva.unidad;
          // Clonamos la matriz física de daños en chasis para la Modalidad A
          this.datosCarroceriaPatio = recepcionActiva.detallesCarroceria ? [...recepcionActiva.detallesCarroceria] : [];
          
          // 🔮 RESOLUCIÓN DINÁMICA DE SILUETA EXACTA (Cero acartonamiento)
          // Sigue la misma cascada exacta de tu código: silueta directa -> tipo de chasis de unidad -> fallback Sedán
          const siluetaBase = recepcionActiva.tipoSilueta || (recepcionActiva.unidad?.tipoCarroceria ? recepcionActiva.unidad.tipoCarroceria : 'Sedán');
          const nombreArchivoSvg = this.mapperCarroceria[siluetaBase] || 'sedan';
          
          // Seteamos la variable que lee dinámicamente tu plantilla HTML
          this.currentVehicleSvg = `images/croquis-${nombreArchivoSvg}.svg`;
          
          console.log(`✅ [SaaS Rampa] Silueta automotriz acoplada en memoria RAM: ${this.currentVehicleSvg}. Pines cargados: ${this.datosCarroceriaPatio.length}`);
        
          this.cdr.detectChanges(); // Forzamos el redibujado de los pines sobre el nuevo chasis
        } else {
          this.toastService.showDanger('Error de Contexto', 'No se encontró el expediente vehicular en el patio.');
        }
      },
      error: (err) => {
        console.error('Error al recuperar expediente del chasis:', err);
        this.toastService.showDanger('Fallo de Red', 'No se pudo precargar el mapa de daños estéticos.');
      }
    });    
  }

  public guardarPresupuesto(): void {
    this.intentarGuardar = true; // Activa las clases .is-invalid visuales
    //if (this.cotizacionForm.invalid || this.lineas.length === 0) {
    if (this.lineas.length === 0) {
      this.toastService.showDanger('Partidas Requeridas', 'No se puede guardar un presupuesto vacío. Ingrese al menos un renglón detallando el trabajo, aunque este se ofrezca con costo \$0.00.');
      this.cdr.detectChanges();
      return;
    }

    if (this.cotizacionForm.invalid) {
      // Marcamos de forma interactiva todos los inputs en rojo usando el motor de validación de Bootstrap (.is-invalid)
      this.cotizacionForm.markAllAsTouched();
      this.lineas.controls.forEach(control => control.markAllAsTouched());
      
      this.toastService.showDanger('Campos Incompletos', 'Por favor, revise los renglones marcados en rojo antes de enviar.');
      this.cdr.detectChanges();
      return;
    }

    // Clonamos el payload para limpiar los valores antes de enviarlos al Backend
    const payload = { ...this.cotizacionForm.getRawValue() };
    
    // Si se guardó con el switch activo, forzamos la conversión estricta a neto antes del envío HTTP
    if (this.ivaIncluido) {
      payload.lineas = payload.lineas.map((l: any) => ({
        ...l,
        precioUnitario: +(l.precioUnitario / 1.16).toFixed(2)
      }));
    }

    console.log('🚀 Payload financiero multi-línea listo para transmisión:', payload);
    // Aquí invocarás tu backend endpoint de Cotizaciones usando HTTP POST

    // Invocación HTTP real hacia Spring Boot
    this.cotizacionesService.save(payload).subscribe({
      next: (res) => {
        this.toastService.showSuccess('Presupuesto Registrado', 'La cotización se ha guardado y el correo electrónico fue enviado.');
        this.router.navigate(['/cotizaciones']); // Regresa a la matriz comercial
      },
      error: (err) => {
        console.error('Error al persistir cotización:', err);
        this.toastService.showDanger('Error de Persistencia', 'El servidor rechazó el payload financiero.');
      }
    });

  }

  public vincularPinACotizacion(pin: any): void {
    // 🛡️ Extracción segura tolerante a snake_case y camelCase
    const tipoDanoReal = pin.tipo_dano || pin.tipoDano || 'DAÑO';
    const descripcionReal = pin.descripcion || 'REPARACIÓN ESTÍTICA';
    const gravedadReal = (pin.gravedad || 'MEDIA').toUpperCase();
    const pinIdReal = pin.id;

    console.log(`📌 [SaaS Rampa] Ejecutando Modalidad A para Pin ID #${pinIdReal} (${tipoDanoReal})`);

    // Sugerimos un precio base automatizado según la gravedad de la inspección
    let costoSugerido = 1200;
    if (gravedadReal === 'ALTA') costoSugerido = 3500;
    else if (gravedadReal === 'MEDIA') costoSugerido = 2200;

    // 🔮 RESOLUCIÓN DINÁMICA ERP: Si el arreglo 'listaUdcsGravedad' tiene datos, sobreescribe el costo
    if (this.listaUdcsGravedad && this.listaUdcsGravedad.length > 0) {
      const configUdc = this.listaUdcsGravedad.find(u => u.strValue1.toUpperCase() === gravedadReal);
      if (configUdc && configUdc.intValue > 0) {
        costoSugerido = configUdc.intValue;
        console.log(`💰 [SaaS UDC Link] Tarifa aplicada desde UDC: $${costoSugerido}.00`);
      }
    }

    const descripcionPropuesta = `REPARACIÓN Y LAMINADO DE ${tipoDanoReal.toUpperCase()} - ${descripcionReal.toUpperCase()}`;
    // 🚀 Inyectamos la línea contable al FormArray amarrando la FK idCarroceriaLink
    this.agregarLinea('Mano de Obra', descripcionPropuesta, costoSugerido, pinIdReal);
    this.toastService.showSuccess('Línea Acoplada', `Daño por ${tipoDanoReal.toUpperCase()} convertido en renglón de presupuesto.`);
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  public alCancelar(): void {
    // 🧭 Regresa limpiamente a la cuadrícula de recepciones
    this.router.navigate(['/recepciones']);
  }

}
